// ============================================
// COMPANYUNPACKVALUESPAGE.JSX - Company Unpack Values Sub-Page
// ============================================

import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetCompanyByIdQuery,
  useGetTemplatesForCompanyQuery,
  useGetFormTemplatesQuery,
  useAssignTemplateToCompanyMutation,
  useUnassignTemplateFromCompanyMutation,
  useDeleteFormTemplateMutation,
  useLazyGetFormTemplateByIdQuery,
} from "../../../services/api";
import TemplateFormModal from "./TemplateFormModal";
import { IconCircle } from "./components/Icons";
import MetricsGrid from "./components/MetricsGrid";
import TemplateCard from "./components/TemplateCard";
import UnpackValuesResults from "./components/UnpackValuesResults";
import DeleteConfirmModal from "./components/modals/DeleteConfirmModal";
import "./CompanyDetails.css";

/**
 * CompanyUnpackValuesPage Component
 * Sub-page for managing company unpack values templates and results
 */
export default function CompanyUnpackValuesPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // ==================== API QUERIES ====================
  const { data: companyData } = useGetCompanyByIdQuery(id);
  const companyName = companyData?.name || "Loading...";

  const { data: assignedTemplates = [], refetch: refetchAssignedTemplates } =
    useGetTemplatesForCompanyQuery(id);

  const { data: allTemplatesData, isLoading: availableTemplatesLoading, refetch: refetchAllTemplates } =
    useGetFormTemplatesQuery({ status: "published", limit: 100 });
  const availableTemplates = allTemplatesData?.data || allTemplatesData?.templates || [];

  // ==================== MUTATIONS ====================
  const [assignTemplate, { isLoading: isAssigning }] = useAssignTemplateToCompanyMutation();
  const [unassignTemplate, { isLoading: isUnassigning }] = useUnassignTemplateFromCompanyMutation();
  const [deleteTemplate, { isLoading: isDeleting }] = useDeleteFormTemplateMutation();
  const [fetchTemplateById, { isLoading: isLoadingTemplate }] = useLazyGetFormTemplateByIdQuery();

  // ==================== UI STATE ====================
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [selectedSubmissionTemplate, setSelectedSubmissionTemplate] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editModal, setEditModal] = useState({ isOpen: false, template: null });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, template: null });

  // ==================== HANDLERS ====================
  const handleDirectAssign = async (templateId) => {
    try {
      await assignTemplate({ templateId, companyId: id, jobLevels: [] }).unwrap();
      refetchAssignedTemplates();
    } catch (error) {
      console.error("Failed to assign template:", error);
    }
  };

  const handleUnassignTemplate = async (templateId) => {
    if (!window.confirm("Are you sure you want to unassign this template?")) return;
    try {
      await unassignTemplate({ templateId, companyId: id }).unwrap();
      refetchAssignedTemplates();
    } catch (error) {
      console.error("Failed to unassign template:", error);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!deleteModal.template) return;
    try {
      await deleteTemplate(deleteModal.template._id).unwrap();
      setDeleteModal({ isOpen: false, template: null });
      refetchAllTemplates();
    } catch (error) {
      console.error("Failed to delete template:", error);
    }
  };

  const handleEditClick = async (template) => {
    try {
      const fullTemplate = await fetchTemplateById(template._id).unwrap();
      setEditModal({ isOpen: true, template: fullTemplate });
    } catch (error) {
      console.error("Failed to fetch template:", error);
    }
  };

  const handleViewAnswers = (templateId) => {
    setSelectedSubmissionTemplate(templateId);
    document.querySelector('.cd-results-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFormModalClose = () => {
    setIsFormModalOpen(false);
    refetchAllTemplates();
  };

  // ==================== METRICS ====================
  const metrics = [
    { icon: <IconCircle />, label: "Templates Assigned", value: assignedTemplates?.length?.toString() || "0" },
  ];

  // ==================== RENDER ====================
  return (
    <>
      <div className="cd-company-details">
        <div className="welcome" onClick={() => navigate(`/admin/companies/${id}`)}>
          ← Back to {companyName}
        </div>
        <h1 className="username">{companyName} - Unpack Values</h1>

        <MetricsGrid metrics={metrics} />

        {/* Assign Unpack Values Section */}
        <div className="cd-card-block cd-no-padding">
          <div className="cd-section-header-simple">
            <div className="cd-section-title-main">Assign Unpack Values</div>
            <button className="cd-add-btn" onClick={() => setIsFormModalOpen(true)}>
              + Add New
            </button>
          </div>

          <div className="cd-section-subtitle">Assign From Available Templates</div>

          {availableTemplatesLoading && (
            <div className="cd-loading">Loading templates...</div>
          )}

          {!availableTemplatesLoading && availableTemplates.length === 0 && (
            <div className="cd-empty-state">
              <p>No templates available. Create one using "+ Add New".</p>
            </div>
          )}

          {!availableTemplatesLoading && availableTemplates.length > 0 && (
            <div className="cd-diagnostic-grid">
              {availableTemplates.map((template) => (
                <TemplateCard
                  key={template._id}
                  template={template}
                  isAssigned={assignedTemplates.some((at) => at._id === template._id)}
                  isAssigning={isAssigning}
                  isUnassigning={isUnassigning}
                  isLoadingTemplate={isLoadingTemplate}
                  onEdit={handleEditClick}
                  onDelete={(t) => setDeleteModal({ isOpen: true, template: t })}
                  onAssign={handleDirectAssign}
                  onUnassign={handleUnassignTemplate}
                  onViewAnswers={handleViewAnswers}
                />
              ))}
            </div>
          )}
        </div>

        <UnpackValuesResults
          companyId={id}
          assignedTemplates={assignedTemplates}
          selectedTemplateId={selectedSubmissionTemplate}
          onSelectTemplate={setSelectedSubmissionTemplate}
          expandedQuestions={expandedQuestions}
          setExpandedQuestions={setExpandedQuestions}
        />
      </div>

      {/* Modals */}
      {isFormModalOpen && (
        <TemplateFormModal template={null} onClose={handleFormModalClose} />
      )}

      {editModal.isOpen && (
        <TemplateFormModal
          template={editModal.template}
          onClose={() => {
            setEditModal({ isOpen: false, template: null });
            refetchAllTemplates();
          }}
        />
      )}

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, template: null })}
        onConfirm={handleDeleteTemplate}
        isDeleting={isDeleting}
        itemName={deleteModal.template?.title}
        warningText="This action cannot be undone. Existing submissions will be preserved."
      />
    </>
  );
}
